import React, { useEffect, useState, useRef } from "react";
import api, { fileUrl } from "@/lib/api";
import Header from "@/components/Header";
import { formatPrice } from "@/components/ProductCard";
import ProductForm from "@/components/ProductForm";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  Package, Tag, ChartLineUp, Warning, Star, Fire, ShieldWarning, Plus, PencilSimple, Trash,
  ArrowUp, ArrowDown, X, UploadSimple, ArrowsDownUp, ChatCircleDots, Receipt, Cube, CheckCircle,
} from "@phosphor-icons/react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { BannersTab, HomeTab, CouponsTab, ConfigTab } from "@/components/AdminExtras";
import { ReportsTab, PaymentsTab, OrdersTab } from "@/components/AdminReports";
import { ClaimsAdminTab } from "@/components/AdminClaims";
import { InvoicesAdminTab } from "@/components/AdminInvoices";

const TABS = [["resumen", "Resumen"], ["reportes", "Reportes"], ["productos", "Productos"], ["categorias", "Categorías"], ["inventario", "Inventario"], ["banners", "Banners"], ["home", "Home"], ["pedidos", "Pedidos"], ["reclamos", "Reclamos"], ["facturas", "Facturas"], ["cupones", "Cupones"], ["preguntas", "Preguntas"], ["pagos", "Pagos"], ["config", "Configuración"]];
const INV_TABS = [["actual", "Inventario actual"], ["agotados", "Agotados"], ["bajo", "Bajo stock"], ["vendidos", "Más vendidos"], ["movimientos", "Movimientos"]];

export default function AdminPanel() {
  const { user, loading: authLoading, login } = useAuth();
  const [tab, setTab] = useState("resumen");
  const [invTab, setInvTab] = useState("actual");
  const [summary, setSummary] = useState(null);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [outStock, setOutStock] = useState([]);
  const [lowStock, setLowStock] = useState([]);
  const [bestSellers, setBestSellers] = useState([]);
  const [movements, setMovements] = useState([]);

  const [showProductForm, setShowProductForm] = useState(false);
  const [editProduct, setEditProduct] = useState(null);
  const [answerText, setAnswerText] = useState({});

  // category form
  const [catForm, setCatForm] = useState(null); // null=closed, {} or existing
  const [showMovement, setShowMovement] = useState(false);

  const loadCore = async () => {
    const [s, p, c] = await Promise.all([api.get("/admin/summary"), api.get("/admin/products"), api.get("/admin/categories")]);
    setSummary(s.data); setProducts(p.data); setCategories(c.data);
  };
  const loadQuestions = async () => setQuestions((await api.get("/admin/questions")).data);
  const loadInventory = async () => {
    const [inv, out, low, best, mov] = await Promise.all([
      api.get("/admin/inventory"), api.get("/admin/inventory/out-of-stock"),
      api.get("/admin/inventory/low-stock"), api.get("/admin/inventory/best-sellers"),
      api.get("/admin/inventory/movements"),
    ]);
    setInventory(inv.data); setOutStock(out.data); setLowStock(low.data); setBestSellers(best.data); setMovements(mov.data);
  };

  useEffect(() => { if (user?.is_admin) loadCore(); }, [user]);
  useEffect(() => {
    if (!user?.is_admin) return;
    if (tab === "preguntas") loadQuestions();
    if (tab === "inventario") loadInventory();
  }, [tab, user]);

  const refreshAll = async () => { await loadCore(); if (tab === "inventario") loadInventory(); };

  const deleteProduct = async (id) => {
    if (!window.confirm("¿Eliminar este producto?")) return;
    await api.delete(`/admin/products/${id}`);
    toast.success("Producto eliminado"); refreshAll();
  };

  const deleteCategory = async (id) => {
    if (!window.confirm("¿Eliminar esta categoría?")) return;
    try { await api.delete(`/admin/categories/${id}`); toast.success("Categoría eliminada"); loadCore(); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const moveCategory = async (idx, dir) => {
    const arr = [...categories];
    const j = idx + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[idx], arr[j]] = [arr[j], arr[idx]];
    setCategories(arr);
    await api.put("/admin/categories/reorder/all", { order: arr.map((c) => c.id) });
  };

  const answer = async (qid) => {
    const text = (answerText[qid] || "").trim();
    if (!text) return;
    await api.post(`/questions/${qid}/answer`, { text });
    setAnswerText((s) => { const c = { ...s }; delete c[qid]; return c; });
    toast.success("Respondido"); loadQuestions();
  };

  const toggleAttended = async (qid, val) => {
    await api.put(`/questions/${qid}/attended`, { is_attended: val });
    loadQuestions();
  };

  if (authLoading) return <div className="App min-h-screen bg-background"><Header /></div>;
  if (!user || !user.is_admin) {
    return (
      <div className="App min-h-screen bg-background">
        <Header />
        <div className="max-w-md mx-auto px-4 py-16 text-center">
          <div className="bg-white rounded-lg nb-shadow p-8" data-testid="admin-denied">
            <ShieldWarning weight="bold" size={40} className="mx-auto text-primary" />
            <h1 className="text-2xl font-extrabold tracking-tight mt-4">Acceso restringido</h1>
            <p className="text-muted-foreground mt-2">Solo administradores pueden ver este panel.</p>
            {!user && <button onClick={login} className="mt-6 w-full nb-btn bg-primary text-primary-foreground py-3 hover:brightness-110">Ingresar</button>}
          </div>
        </div>
      </div>
    );
  }

  const stats = [
    { label: "Productos", value: summary?.total_products ?? 0, icon: Package },
    { label: "Activos", value: summary?.active_products ?? 0, icon: CheckCircle },
    { label: "Categorías", value: summary?.total_categories ?? 0, icon: Tag },
    { label: "Destacados", value: summary?.featured_products ?? 0, icon: Star },
    { label: "En oferta", value: summary?.offer_products ?? 0, icon: Fire },
    { label: "Agotados", value: summary?.out_of_stock ?? 0, icon: Warning, warn: true },
    { label: "Bajo stock", value: summary?.low_stock ?? 0, icon: Warning, warn: true },
    { label: "Pedidos", value: summary?.total_orders ?? 0, icon: Receipt },
    { label: "Ingresos", value: formatPrice(summary?.total_revenue), icon: ChartLineUp, accent: true },
  ];

  const catName = (ids) => (ids || []).map((id) => categories.find((c) => c.id === id)?.name).filter(Boolean).join(", ");

  return (
    <div className="App min-h-screen bg-background">
      <Header />
      <div className="max-w-[1200px] mx-auto px-3 sm:px-5 py-6">
        <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight">Panel de administración</h1>

        {/* Tabs */}
        <div className="flex flex-wrap gap-2 mt-5 border-b border-black/10">
          {TABS.map(([k, label]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`admin-tab-${k}`}
                    className={`text-sm font-semibold px-4 py-2.5 -mb-px border-b-2 transition-colors ${tab === k ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
              {label}
            </button>
          ))}
        </div>

        <div className="mt-6">
          {/* RESUMEN */}
          {tab === "resumen" && (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="summary-grid">
              {stats.map((s, i) => {
                const Icon = s.icon;
                return (
                  <div key={i} className={`rounded-lg p-4 nb-shadow ${s.accent ? "bg-primary text-primary-foreground" : "bg-white"}`}>
                    <Icon weight="bold" size={20} className={s.warn ? "text-destructive" : ""} />
                    <p className="text-xs text-muted-foreground mt-2 uppercase tracking-wide font-semibold" style={s.accent ? { color: "rgba(255,255,255,.8)" } : {}}>{s.label}</p>
                    <p className="text-xl font-extrabold tracking-tight mt-0.5">{s.value}</p>
                  </div>
                );
              })}
            </div>
          )}

          {/* PRODUCTOS */}
          {tab === "productos" && (
            <div>
              <div className="flex justify-between items-center mb-4">
                <span className="text-sm text-muted-foreground">{products.length} productos</span>
                <button onClick={() => { setEditProduct(null); setShowProductForm(true); }} data-testid="new-product-button"
                        className="nb-btn bg-primary text-primary-foreground px-4 py-2.5 flex items-center gap-2 hover:brightness-110">
                  <Plus weight="bold" size={18} /> Nuevo producto
                </button>
              </div>
              <div className="bg-white rounded-lg nb-shadow overflow-x-auto" data-testid="admin-products-table">
                <table className="w-full text-sm">
                  <thead><tr className="border-b border-black/10 text-left text-muted-foreground">
                    <th className="p-3">Producto</th><th className="p-3">Categoría</th><th className="p-3 text-right">Precio</th>
                    <th className="p-3 text-right">Stock</th><th className="p-3">Estado</th><th className="p-3 text-right">Acciones</th>
                  </tr></thead>
                  <tbody>
                    {products.map((p) => (
                      <tr key={p.id} className="border-b border-black/5" data-testid={`admin-product-${p.id}`}>
                        <td className="p-3">
                          <div className="flex items-center gap-2">
                            <img src={p.main_image ? fileUrl(p.main_image) : ""} alt="" className="w-10 h-10 rounded object-cover bg-secondary" />
                            <div><p className="font-medium line-clamp-1">{p.title}</p><p className="text-xs text-muted-foreground">{p.sku}</p></div>
                          </div>
                        </td>
                        <td className="p-3 text-muted-foreground text-xs">{catName(p.category_ids)}</td>
                        <td className="p-3 text-right font-semibold">
                          {formatPrice(p.price)}
                          {p.compare_at_price > p.price && <div className="text-xs text-muted-foreground line-through">{formatPrice(p.compare_at_price)}</div>}
                        </td>
                        <td className="p-3 text-right">{p.total_stock}{p.variants?.length ? <span className="text-xs text-primary block">{p.variants.length} var.</span> : null}</td>
                        <td className="p-3">
                          <div className="flex flex-wrap gap-1">
                            {!p.is_active && <span className="text-[10px] px-1.5 py-0.5 rounded bg-secondary">Inactivo</span>}
                            {(p.is_sold_out || p.total_stock === 0) && <span className="text-[10px] px-1.5 py-0.5 rounded bg-destructive text-white">Agotado</span>}
                            {p.is_featured && <span className="text-[10px] px-1.5 py-0.5 rounded bg-primary text-white">Destacado</span>}
                            {p.is_on_offer && <span className="text-[10px] px-1.5 py-0.5 rounded bg-[hsl(var(--meli-green))] text-white">Oferta</span>}
                          </div>
                        </td>
                        <td className="p-3">
                          <div className="flex gap-2 justify-end">
                            <button onClick={() => { setEditProduct(p); setShowProductForm(true); }} data-testid={`edit-product-${p.id}`} className="nb-btn bg-secondary p-2 hover:bg-primary hover:text-white"><PencilSimple weight="bold" size={15} /></button>
                            <button onClick={() => deleteProduct(p.id)} data-testid={`delete-product-${p.id}`} className="nb-btn bg-secondary p-2 hover:bg-destructive hover:text-white"><Trash weight="bold" size={15} /></button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* CATEGORIAS */}
          {tab === "categorias" && (
            <div>
              <div className="flex justify-between items-center mb-4">
                <span className="text-sm text-muted-foreground">{categories.length} categorías</span>
                <button onClick={() => setCatForm({})} data-testid="new-category-button" className="nb-btn bg-primary text-primary-foreground px-4 py-2.5 flex items-center gap-2 hover:brightness-110">
                  <Plus weight="bold" size={18} /> Nueva categoría
                </button>
              </div>
              <div className="bg-white rounded-lg nb-shadow overflow-x-auto" data-testid="admin-categories-table">
                <table className="w-full text-sm">
                  <thead><tr className="border-b border-black/10 text-left text-muted-foreground">
                    <th className="p-3">Orden</th><th className="p-3">Categoría</th><th className="p-3">Tipo</th><th className="p-3">Estado</th><th className="p-3 text-right">Acciones</th>
                  </tr></thead>
                  <tbody>
                    {categories.map((c, i) => (
                      <tr key={c.id} className="border-b border-black/5" data-testid={`admin-category-${c.id}`}>
                        <td className="p-3">
                          <div className="flex gap-1">
                            <button onClick={() => moveCategory(i, -1)} className="nb-btn bg-secondary p-1"><ArrowUp weight="bold" size={13} /></button>
                            <button onClick={() => moveCategory(i, 1)} className="nb-btn bg-secondary p-1"><ArrowDown weight="bold" size={13} /></button>
                          </div>
                        </td>
                        <td className="p-3">
                          <div className="flex items-center gap-2">
                            {c.image ? <img src={fileUrl(c.image)} alt="" className="w-8 h-8 rounded object-cover" /> : <span className="w-8 h-8 rounded bg-secondary flex items-center justify-center text-xs font-bold">{c.name[0]}</span>}
                            <div><p className="font-medium">{c.parent_id ? "— " : ""}{c.name}</p>{c.description && <p className="text-xs text-muted-foreground line-clamp-1">{c.description}</p>}</div>
                          </div>
                        </td>
                        <td className="p-3 text-xs text-muted-foreground">{c.parent_id ? `Subcat. de ${categories.find((x) => x.id === c.parent_id)?.name || "—"}` : "Principal"}</td>
                        <td className="p-3">{c.is_active ? <span className="text-[10px] px-1.5 py-0.5 rounded bg-[hsl(var(--meli-green))] text-white">Activa</span> : <span className="text-[10px] px-1.5 py-0.5 rounded bg-secondary">Inactiva</span>}</td>
                        <td className="p-3">
                          <div className="flex gap-2 justify-end">
                            <button onClick={() => setCatForm(c)} data-testid={`edit-category-${c.id}`} className="nb-btn bg-secondary p-2 hover:bg-primary hover:text-white"><PencilSimple weight="bold" size={15} /></button>
                            <button onClick={() => deleteCategory(c.id)} data-testid={`delete-category-${c.id}`} className="nb-btn bg-secondary p-2 hover:bg-destructive hover:text-white"><Trash weight="bold" size={15} /></button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* INVENTARIO */}
          {tab === "inventario" && (
            <div>
              <div className="flex flex-wrap justify-between items-center gap-2 mb-4">
                <div className="flex flex-wrap gap-2">
                  {INV_TABS.map(([k, label]) => (
                    <button key={k} onClick={() => setInvTab(k)} data-testid={`inv-tab-${k}`}
                            className={`text-xs font-semibold px-3 py-1.5 rounded-full ${invTab === k ? "bg-primary text-primary-foreground" : "bg-white text-muted-foreground hover:bg-secondary"}`}>{label}</button>
                  ))}
                </div>
                <button onClick={() => setShowMovement(true)} data-testid="new-movement-button" className="nb-btn bg-primary text-primary-foreground px-4 py-2 flex items-center gap-2 text-sm hover:brightness-110">
                  <ArrowsDownUp weight="bold" size={16} /> Registrar movimiento
                </button>
              </div>

              {invTab === "movimientos" ? (
                <div className="bg-white rounded-lg nb-shadow overflow-x-auto" data-testid="movements-table">
                  <table className="w-full text-sm">
                    <thead><tr className="border-b border-black/10 text-left text-muted-foreground"><th className="p-3">Fecha</th><th className="p-3">Producto</th><th className="p-3">Tipo</th><th className="p-3 text-right">Cantidad</th><th className="p-3 text-right">Saldo</th><th className="p-3">Motivo</th></tr></thead>
                    <tbody>
                      {movements.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-muted-foreground">Sin movimientos</td></tr>}
                      {movements.map((m) => (
                        <tr key={m.id} className="border-b border-black/5">
                          <td className="p-3 text-muted-foreground text-xs">{new Date(m.created_at).toLocaleString("es-MX")}</td>
                          <td className="p-3">{m.product_title}{m.variant_name && <span className="text-xs text-primary"> · {m.variant_name}</span>}</td>
                          <td className="p-3"><span className={`text-[10px] px-1.5 py-0.5 rounded ${m.type === "entrada" ? "bg-[hsl(var(--meli-green))] text-white" : "bg-destructive text-white"}`}>{m.type}</span></td>
                          <td className="p-3 text-right font-semibold">{m.type === "entrada" ? "+" : "-"}{m.quantity}</td>
                          <td className="p-3 text-right">{m.balance_after ?? "—"}</td>
                          <td className="p-3 text-muted-foreground text-xs">{m.reason}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : invTab === "vendidos" ? (
                <div className="bg-white rounded-lg nb-shadow overflow-x-auto" data-testid="bestsellers-table">
                  <table className="w-full text-sm">
                    <thead><tr className="border-b border-black/10 text-left text-muted-foreground"><th className="p-3">#</th><th className="p-3">Producto</th><th className="p-3 text-right">Unidades</th><th className="p-3 text-right">Ingresos</th></tr></thead>
                    <tbody>
                      {bestSellers.length === 0 && <tr><td colSpan={4} className="p-6 text-center text-muted-foreground">Aún sin ventas</td></tr>}
                      {bestSellers.map((b, i) => (
                        <tr key={b.product_id} className="border-b border-black/5"><td className="p-3 font-bold">{i + 1}</td><td className="p-3">{b.product_title}</td><td className="p-3 text-right font-semibold">{b.units}</td><td className="p-3 text-right">{formatPrice(b.revenue)}</td></tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="bg-white rounded-lg nb-shadow overflow-x-auto" data-testid={`inventory-${invTab}`}>
                  <table className="w-full text-sm">
                    <thead><tr className="border-b border-black/10 text-left text-muted-foreground"><th className="p-3">Producto</th><th className="p-3">SKU</th><th className="p-3 text-right">Stock total</th><th className="p-3">Detalle variantes</th></tr></thead>
                    <tbody>
                      {(invTab === "actual" ? inventory : invTab === "agotados" ? outStock : lowStock).map((p) => (
                        <tr key={p.id} className="border-b border-black/5">
                          <td className="p-3 font-medium">{p.title}</td>
                          <td className="p-3 text-muted-foreground text-xs">{p.sku}</td>
                          <td className={`p-3 text-right font-bold ${p.total_stock === 0 ? "text-destructive" : p.total_stock <= 5 ? "text-orange-600" : ""}`}>{p.total_stock}</td>
                          <td className="p-3 text-xs text-muted-foreground">{(p.variants || []).map((v) => `${v.name}: ${v.stock}`).join(" · ") || "—"}</td>
                        </tr>
                      ))}
                      {(invTab === "actual" ? inventory : invTab === "agotados" ? outStock : lowStock).length === 0 && <tr><td colSpan={4} className="p-6 text-center text-muted-foreground">Sin productos</td></tr>}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* BANNERS */}
          {tab === "banners" && <BannersTab products={products} categories={categories} />}

          {/* HOME SECTIONS */}
          {tab === "home" && <HomeTab />}

          {/* CUPONES */}
          {tab === "cupones" && <CouponsTab />}

          {/* CONFIG */}
          {tab === "config" && <ConfigTab />}

          {/* REPORTES */}
          {tab === "reportes" && <ReportsTab />}

          {/* PAGOS */}
          {tab === "pagos" && <PaymentsTab />}

          {/* PEDIDOS */}
          {tab === "pedidos" && <OrdersTab />}

          {/* RECLAMOS */}
          {tab === "reclamos" && <ClaimsAdminTab />}

          {/* FACTURAS */}
          {tab === "facturas" && <InvoicesAdminTab />}

          {/* PREGUNTAS */}
          {tab === "preguntas" && (
            <div className="space-y-3" data-testid="admin-questions">
              {questions.length === 0 && <p className="text-muted-foreground">Sin preguntas</p>}
              {questions.map((q) => (
                <div key={q.id} className="bg-white rounded-lg nb-shadow p-4">
                  <div className="flex gap-2 items-start"><ChatCircleDots weight="bold" size={18} className="text-muted-foreground mt-0.5" />
                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-medium text-sm">{q.text}</p>
                        {q.is_attended
                          ? <span className="text-[10px] px-1.5 py-0.5 rounded bg-[hsl(var(--meli-green))]/15 text-[hsl(var(--meli-green))] font-semibold">Atendida</span>
                          : <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 font-semibold">Pendiente</span>}
                      </div>
                      <p className="text-xs text-muted-foreground">{q.user_name} · {new Date(q.created_at).toLocaleDateString("es-MX")}</p>
                      {q.answer && (
                        <p className="mt-2 text-sm text-foreground/80 pl-3 border-l-2 border-primary/30">
                          {q.answer}
                          <span className="block text-[11px] text-muted-foreground mt-0.5">Respondió {q.answered_by || "admin"}{q.answered_at ? ` · ${new Date(q.answered_at).toLocaleDateString("es-MX")}` : ""}</span>
                        </p>
                      )}
                      <div className="mt-2 flex gap-2 flex-wrap items-center">
                        <input value={answerText[q.id] !== undefined ? answerText[q.id] : (q.answer || "")}
                               onChange={(e) => setAnswerText((s) => ({ ...s, [q.id]: e.target.value }))}
                               placeholder="Escribe una respuesta…" data-testid={`admin-answer-input-${q.id}`}
                               className="flex-1 min-w-[200px] border border-black/10 rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30" />
                        <button onClick={() => answer(q.id)} data-testid={`admin-answer-button-${q.id}`} className="nb-btn bg-primary text-primary-foreground px-4 py-1.5 text-sm hover:brightness-110">{q.answer ? "Actualizar" : "Responder"}</button>
                        <button onClick={() => toggleAttended(q.id, !q.is_attended)} data-testid={`admin-attended-button-${q.id}`}
                                className={`nb-btn px-3 py-1.5 text-sm flex items-center gap-1 ${q.is_attended ? "bg-secondary text-foreground" : "bg-[hsl(var(--meli-green))]/15 text-[hsl(var(--meli-green))]"}`}>
                          <CheckCircle weight="bold" size={15} /> {q.is_attended ? "Marcar pendiente" : "Marcar atendida"}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <ProductForm open={showProductForm} onClose={() => setShowProductForm(false)}
                   onSaved={() => { setShowProductForm(false); refreshAll(); }}
                   initial={editProduct} categories={categories} allProducts={products} />

      {catForm !== null && <CategoryForm initial={catForm.id ? catForm : null} categories={categories}
                                         onClose={() => setCatForm(null)} onSaved={() => { setCatForm(null); loadCore(); }} />}

      {showMovement && <MovementForm products={products} onClose={() => setShowMovement(false)}
                                     onSaved={() => { setShowMovement(false); loadInventory(); loadCore(); }} />}
    </div>
  );
}

// ---------- Category form modal ----------
function CategoryForm({ initial, categories, onClose, onSaved }) {
  const [form, setForm] = useState(initial || { name: "", description: "", image: null, parent_id: null, is_active: true, is_adult: false, order: 0 });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();

  const upload = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setUploading(true);
    try { const fd = new FormData(); fd.append("file", f); const res = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }); setForm((s) => ({ ...s, image: res.data.path })); }
    catch { toast.error("Error al subir"); } finally { setUploading(false); }
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.name) { toast.error("Ingresa un nombre"); return; }
    setSaving(true);
    try {
      const payload = { name: form.name, description: form.description || "", image: form.image || null, parent_id: form.parent_id || null, is_active: form.is_active, is_adult: !!form.is_adult, order: form.order || 0 };
      if (initial) await api.put(`/admin/categories/${initial.id}`, payload);
      else await api.post("/admin/categories", payload);
      toast.success(initial ? "Categoría actualizada" : "Categoría creada"); onSaved();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); } finally { setSaving(false); }
  };

  const inputCls = "w-full border border-black/15 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30";
  const parents = categories.filter((c) => !c.parent_id && (!initial || c.id !== initial.id));

  return (
    <div className="fixed inset-0 z-[60] bg-foreground/40 flex items-start justify-center p-3 overflow-y-auto">
      <div className="bg-white rounded-lg nb-shadow-lg w-full max-w-md my-8" data-testid="category-form-modal">
        <div className="flex items-center justify-between border-b border-black/10 p-4">
          <h2 className="text-lg font-extrabold tracking-tight">{initial ? "Editar categoría" : "Nueva categoría"}</h2>
          <button onClick={onClose} className="nb-btn bg-secondary p-1.5"><X weight="bold" size={18} /></button>
        </div>
        <form onSubmit={save} className="p-4 space-y-4">
          <div><label className="text-xs font-bold uppercase tracking-wide">Nombre</label><input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="category-name-input" className={inputCls} /></div>
          <div><label className="text-xs font-bold uppercase tracking-wide">Descripción</label><textarea rows={2} value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} className={inputCls} /></div>
          <div>
            <label className="text-xs font-bold uppercase tracking-wide">Categoría padre (subcategoría)</label>
            <Select value={form.parent_id || "none"} onValueChange={(v) => setForm({ ...form, parent_id: v === "none" ? null : v })}>
              <SelectTrigger className="rounded-md text-sm mt-0.5"><SelectValue /></SelectTrigger>
              <SelectContent className="rounded-md"><SelectItem value="none">Ninguna (principal)</SelectItem>{parents.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-wide">Imagen</label>
            <input ref={fileRef} type="file" accept="image/*" onChange={upload} className="hidden" />
            <div className="flex items-center gap-2 mt-1">
              {form.image && <img src={fileUrl(form.image)} alt="" className="w-12 h-12 rounded object-cover border border-black/10" />}
              <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} className="nb-btn bg-secondary px-3 py-2 text-sm flex items-center gap-1"><UploadSimple weight="bold" size={15} /> {uploading ? "Subiendo…" : "Subir"}</button>
            </div>
          </div>
          <label className="flex items-center justify-between border border-black/10 rounded-md px-3 py-2 text-sm"><span>Activa</span><Switch checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} data-testid="category-active-toggle" /></label>
          <label className="flex items-center justify-between border border-black/10 rounded-md px-3 py-2 text-sm"><span>Contenido para adultos (+18)</span><Switch checked={form.is_adult} onCheckedChange={(v) => setForm({ ...form, is_adult: v })} data-testid="category-adult-toggle" /></label>
          <button type="submit" disabled={saving} data-testid="submit-category-button" className="w-full nb-btn bg-primary text-primary-foreground py-2.5 hover:brightness-110 disabled:opacity-50">{saving ? "Guardando…" : "Guardar"}</button>
        </form>
      </div>
    </div>
  );
}

// ---------- Movement form modal ----------
function MovementForm({ products, onClose, onSaved }) {
  const [productId, setProductId] = useState("");
  const [variantId, setVariantId] = useState("");
  const [type, setType] = useState("entrada");
  const [quantity, setQuantity] = useState("1");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  const product = products.find((p) => p.id === productId);
  const hasVariants = product?.variants?.length > 0;

  const save = async (e) => {
    e.preventDefault();
    if (!productId) { toast.error("Selecciona un producto"); return; }
    if (hasVariants && !variantId) { toast.error("Selecciona una variante"); return; }
    setSaving(true);
    try {
      await api.post("/admin/inventory/movement", { product_id: productId, variant_id: variantId || null, type, quantity: parseInt(quantity, 10), reason });
      toast.success("Movimiento registrado"); onSaved();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); } finally { setSaving(false); }
  };

  const inputCls = "w-full border border-black/15 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30";

  return (
    <div className="fixed inset-0 z-[60] bg-foreground/40 flex items-start justify-center p-3 overflow-y-auto">
      <div className="bg-white rounded-lg nb-shadow-lg w-full max-w-md my-8" data-testid="movement-form-modal">
        <div className="flex items-center justify-between border-b border-black/10 p-4">
          <h2 className="text-lg font-extrabold tracking-tight">Registrar movimiento</h2>
          <button onClick={onClose} className="nb-btn bg-secondary p-1.5"><X weight="bold" size={18} /></button>
        </div>
        <form onSubmit={save} className="p-4 space-y-4">
          <div>
            <label className="text-xs font-bold uppercase tracking-wide">Producto</label>
            <Select value={productId} onValueChange={(v) => { setProductId(v); setVariantId(""); }}>
              <SelectTrigger data-testid="movement-product" className="rounded-md text-sm mt-0.5"><SelectValue placeholder="Selecciona…" /></SelectTrigger>
              <SelectContent className="rounded-md max-h-60">{products.map((p) => <SelectItem key={p.id} value={p.id}>{p.title}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {hasVariants && (
            <div>
              <label className="text-xs font-bold uppercase tracking-wide">Variante</label>
              <Select value={variantId} onValueChange={setVariantId}>
                <SelectTrigger data-testid="movement-variant" className="rounded-md text-sm mt-0.5"><SelectValue placeholder="Selecciona…" /></SelectTrigger>
                <SelectContent className="rounded-md">{product.variants.map((v) => <SelectItem key={v.id} value={v.id}>{v.name} (stock: {v.stock})</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-bold uppercase tracking-wide">Tipo</label>
              <Select value={type} onValueChange={setType}>
                <SelectTrigger data-testid="movement-type" className="rounded-md text-sm mt-0.5"><SelectValue /></SelectTrigger>
                <SelectContent className="rounded-md"><SelectItem value="entrada">Entrada</SelectItem><SelectItem value="salida">Salida</SelectItem></SelectContent>
              </Select>
            </div>
            <div><label className="text-xs font-bold uppercase tracking-wide">Cantidad</label><input type="number" min="1" value={quantity} onChange={(e) => setQuantity(e.target.value)} data-testid="movement-quantity" className={inputCls} /></div>
          </div>
          <div><label className="text-xs font-bold uppercase tracking-wide">Motivo</label><input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Compra a proveedor, ajuste, etc." data-testid="movement-reason" className={inputCls} /></div>
          <button type="submit" disabled={saving} data-testid="submit-movement-button" className="w-full nb-btn bg-primary text-primary-foreground py-2.5 hover:brightness-110 disabled:opacity-50">{saving ? "Guardando…" : "Registrar"}</button>
        </form>
      </div>
    </div>
  );
}
